import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type Me, type Level } from "../lib/api";

// Level thresholds — keep in sync with intake/sheet-schema.md.
const NEXT_THRESHOLD: Record<string, number | null> = {
  "Novice": 100,
  "Apprentice": 300,
  "Practitioner": 750,
  "Power User": 1500,
  "Champion": 3000,
  "Ambassador": null,
};

export function Home() {
  const { data: me, isLoading } = useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try { return await api.me(); } catch { return null; }
    },
  });

  const { data: level } = useQuery<Level>({
    queryKey: ["levels", "me"],
    queryFn: api.levels.me,
    enabled: !!me,
  });

  if (isLoading) return <p>Loading…</p>;

  if (!me) {
    return (
      <div className="space-y-3">
        <h1 className="text-xl font-semibold">AI Adoption Platform</h1>
        <p>You're not signed in.</p>
        <Link className="text-blue-600 hover:underline" to="/login">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Welcome, {me.name}</h1>
        <p className="text-sm text-slate-600">{me.function} · {me.highest}</p>
      </div>

      {level ? (
        <section className="border rounded p-4 bg-white">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-sm text-slate-500">Current level</div>
              <div className="text-2xl font-semibold">{level.level}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-500">Total XP</div>
              <div className="text-2xl font-semibold">{level.total_xp}</div>
            </div>
          </div>
          {NEXT_THRESHOLD[level.level] != null ? (
            <ProgressToNext current={level.total_xp} next={NEXT_THRESHOLD[level.level]!} />
          ) : (
            <p className="text-xs text-slate-600 mt-2">Top of the ladder.</p>
          )}
          <div className="grid grid-cols-3 gap-3 mt-4 text-center text-sm">
            <Stat label="Wins logged" value={level.wins_logged} />
            <Stat label="Wins verified" value={level.wins_verified} />
            <Stat
              label="Weeks since last submission"
              value={level.weeks_since_last_submission ?? "—"}
              warn={level.weeks_since_last_submission != null && level.weeks_since_last_submission >= 2}
            />
          </div>
        </section>
      ) : null}

      <section className="flex flex-wrap gap-2">
        <Link to="/weekly" className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Submit weekly intake</Link>
        <Link to="/baseline" className="px-3 py-1 rounded bg-slate-200 text-sm">Take baseline survey</Link>
        <Link to="/wins/mine" className="px-3 py-1 rounded bg-slate-200 text-sm">My wins</Link>
        {(me.roles.includes("manager") || me.roles.includes("strategist") || me.roles.includes("ceo")) ? (
          <Link to="/attest" className="px-3 py-1 rounded bg-slate-200 text-sm">Attestations</Link>
        ) : null}
        {(me.roles.includes("champion") || me.roles.includes("strategist") || me.roles.includes("ceo")) ? (
          <>
            <Link to="/dashboard" className="px-3 py-1 rounded bg-slate-200 text-sm">Dashboard</Link>
            <Link to="/leaderboard" className="px-3 py-1 rounded bg-slate-200 text-sm">Leaderboard</Link>
          </>
        ) : null}
      </section>
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: number | string; warn?: boolean }) {
  return (
    <div className={`rounded p-2 ${warn ? "bg-amber-50 border border-amber-300" : "bg-slate-50 border border-slate-200"}`}>
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-xs text-slate-600 mt-1">{label}</div>
    </div>
  );
}

function ProgressToNext({ current, next }: { current: number; next: number }) {
  const pct = Math.min(100, Math.floor((current / next) * 100));
  return (
    <div className="mt-3">
      <div className="bg-slate-100 rounded h-2 overflow-hidden">
        <div className="bg-blue-600 h-full" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-slate-600 mt-1">{current} / {next} XP to next level</div>
    </div>
  );
}
